import React from 'react'
import PropTypes from 'prop-types'
import {
  AddGoalsCopy,
  AddGoalsWrapper,
  CheckboxWrapper,
  CheckIconWrapper,
  DateTitle,
  DateWrapper,
  Error,
  ErrorText,
  HiddenCheckbox,
  InputsWrapper,
  OptionalGoalsSection,
  NumberError,
  NumberTitle,
  NumberWrapper,
  PreviousYearText,
  StyledAdminTooltip,
  StyledCalendarInput,
  StyledCheckbox,
  StyledCheckIcon,
  StyledFormInputNumber,
  StyledHr,
  StyledWarningIcon,
} from './component.styles'
import { StyledHeader } from '../component.styles'
import RequiredGoal from '../RequiredGoal'
import OptionalGoals from '../OptionalGoals'
import {
  classCreationGoalValueName,
  classCreationGoalDateName,
  lessonCompletionGoalValueName,
  lessonCompletionGoalDateName,
  programStartDateName,
  schoolwideGoalIsDisabledName,
} from './data.js'

const goalValueFieldNames = [
  classCreationGoalValueName,
  lessonCompletionGoalValueName,
]

const AddGoals = ({
  additionalGoalCount,
  modalData,
  values,
  errors,
  preferences,
  interactedFields,
  touched,
  handleChange,
  handleChangeGoalCount,
}) => {
  const { bodyCopy, choiceContent, header } = modalData || {}
  const [classCreationData, lessonCompletionData] = choiceContent || {}
  const showPreviousYearData = !preferences?.previousYear?.previousYearText?.endsWith(
    '25-26',
  )
  const hasSavedValue = fieldName =>
    goalValueFieldNames.includes(fieldName)
      ? preferences?.[fieldName] != null
      : Boolean(preferences?.[fieldName])
  const shouldUsePlaceholderColor = fieldName =>
    !interactedFields?.[fieldName] && !hasSavedValue(fieldName)

  return (
    <AddGoalsWrapper>
      <StyledHeader>{header}</StyledHeader>
      <AddGoalsCopy>{bodyCopy}</AddGoalsCopy>
      <RequiredGoal
        dataTestId={'class-creation-goal'}
        description={classCreationData.bodyCopy}
        title={classCreationData.h3}
      >
        <InputsWrapper>
          <NumberWrapper>
            <NumberTitle>Number of Classes Created</NumberTitle>
            <StyledFormInputNumber
              dataTestId={classCreationGoalValueName}
              error={
                touched[classCreationGoalValueName] &&
                errors[classCreationGoalValueName]
              }
              onChange={value => {
                handleChange(classCreationGoalValueName, value)
              }}
              placeholder="Enter number of classes"
              usePlaceholderColor={shouldUsePlaceholderColor(
                classCreationGoalValueName,
              )}
              value={values[classCreationGoalValueName]}
            />
            {showPreviousYearData &&
              preferences?.ClassesCreated != null &&
              values.previousYear?.ClassCreationGoalValue != null &&
              preferences?.previousYear?.previousYearText && (
                <PreviousYearText>{`${preferences.previousYear.previousYearText} classes created: ${preferences?.ClassesCreated} | Goal: ${values.previousYear.ClassCreationGoalValue}`}</PreviousYearText>
              )}
            {touched[classCreationGoalValueName] &&
              errors[classCreationGoalValueName] && (
                <NumberError>
                  <StyledWarningIcon />
                  <ErrorText>{errors[classCreationGoalValueName]}</ErrorText>
                </NumberError>
              )}
          </NumberWrapper>
          <DateWrapper>
            <DateTitle>Complete By</DateTitle>
            <StyledCalendarInput
              date={values[classCreationGoalDateName]}
              handleChange={handleChange}
              hasError={
                !!errors[classCreationGoalDateName] &&
                !!touched[classCreationGoalDateName]
              }
              name={classCreationGoalDateName}
              showNeighboringMonth={false}
              usePlaceholderColor={shouldUsePlaceholderColor(
                classCreationGoalDateName,
              )}
            />
            {errors[classCreationGoalDateName] &&
              touched[classCreationGoalDateName] && (
                <Error>
                  <StyledWarningIcon />
                  <ErrorText>{errors[classCreationGoalDateName]}</ErrorText>
                </Error>
              )}
          </DateWrapper>
        </InputsWrapper>
      </RequiredGoal>

      <RequiredGoal
        dataTestId={'lesson-completion-goal'}
        description={lessonCompletionData.bodyCopy}
        recommendation={'60%-80% Recommended'}
        title={lessonCompletionData.h3}
      >
        <InputsWrapper>
          <NumberWrapper>
            <NumberTitle>% of Lessons Completed</NumberTitle>
            <StyledFormInputNumber
              dataTestId={lessonCompletionGoalValueName}
              disabled={values.LessonCompletionGoalIsDisabled}
              error={
                touched[lessonCompletionGoalValueName] &&
                errors[lessonCompletionGoalValueName]
              }
              onChange={value => {
                handleChange(lessonCompletionGoalValueName, value)
              }}
              placeholder="Enter number"
              usePlaceholderColor={shouldUsePlaceholderColor(
                lessonCompletionGoalValueName,
              )}
              value={values[lessonCompletionGoalValueName]}
            />
            {showPreviousYearData &&
              preferences?.LessonCompletionPercentage != null &&
              values.previousYear?.LessonCompletionGoalValue != null &&
              preferences?.previousYear?.previousYearText && (
                <PreviousYearText>{`${preferences.previousYear.previousYearText} completion: ${preferences?.LessonCompletionPercentage}% | Goal: ${values.previousYear.LessonCompletionGoalValue}%`}</PreviousYearText>
              )}
            {touched[lessonCompletionGoalValueName] &&
              errors[lessonCompletionGoalValueName] && (
                <NumberError>
                  <StyledWarningIcon />
                  <ErrorText>{errors[lessonCompletionGoalValueName]}</ErrorText>
                </NumberError>
              )}
          </NumberWrapper>
          <DateWrapper>
            <DateTitle>Start Date</DateTitle>
            <StyledCalendarInput
              date={values[programStartDateName]}
              disabled={values.LessonCompletionGoalIsDisabled}
              error={errors?.[programStartDateName]}
              handleChange={handleChange}
              hasError={
                !!errors[programStartDateName] &&
                !!touched[programStartDateName]
              }
              name={programStartDateName}
              showNeighboringMonth={false}
              usePlaceholderColor={shouldUsePlaceholderColor(
                programStartDateName,
              )}
            />
            {errors[programStartDateName] && touched[programStartDateName] && (
              <Error>
                <StyledWarningIcon />
                <ErrorText>{errors[programStartDateName]}</ErrorText>
              </Error>
            )}
          </DateWrapper>
          <DateWrapper>
            <DateTitle>Complete By</DateTitle>
            <StyledCalendarInput
              date={values[lessonCompletionGoalDateName]}
              disabled={values.LessonCompletionGoalIsDisabled}
              error={errors?.[lessonCompletionGoalDateName]}
              handleChange={handleChange}
              hasError={
                !!errors[lessonCompletionGoalDateName] &&
                !!touched[lessonCompletionGoalDateName]
              }
              name={lessonCompletionGoalDateName}
              showNeighboringMonth={false}
              usePlaceholderColor={shouldUsePlaceholderColor(
                lessonCompletionGoalDateName,
              )}
            />
            {errors[lessonCompletionGoalDateName] &&
              touched[lessonCompletionGoalDateName] && (
                <Error>
                  <StyledWarningIcon />
                  <ErrorText>{errors[lessonCompletionGoalDateName]}</ErrorText>
                </Error>
              )}
          </DateWrapper>
        </InputsWrapper>
        <StyledHr />
        <CheckboxWrapper>
          <HiddenCheckbox
            checked={values.LessonCompletionGoalIsDisabled}
            id={schoolwideGoalIsDisabledName}
            name={schoolwideGoalIsDisabledName}
            onChange={e => {
              handleChange(schoolwideGoalIsDisabledName, e.target.checked)
              if (
                !values.LessonCompletionGoalIsDisabled &&
                !values.Goal3Text &&
                !values.Goal3Date
              ) {
                handleChangeGoalCount(1)
              }
              if (
                values.LessonCompletionGoalIsDisabled &&
                !values.Goal3Text &&
                !values.Goal3Date &&
                additionalGoalCount > 0
              ) {
                handleChangeGoalCount(-1)
              }
            }}
            type="checkbox"
          />
          <StyledCheckbox htmlFor={schoolwideGoalIsDisabledName}>
            <CheckIconWrapper>
              <StyledCheckIcon />
            </CheckIconWrapper>
          </StyledCheckbox>
          <AddGoalsCopy>
            Setting a schoolwide goal isn't relevant for my school
          </AddGoalsCopy>
          <StyledAdminTooltip tooltip={modalData?.tooltip?.[0]} />
        </CheckboxWrapper>
      </RequiredGoal>

      <OptionalGoalsSection>
        <OptionalGoals
          additionalGoalCount={additionalGoalCount}
          errors={errors}
          handleChange={handleChange}
          handleChangeGoalCount={handleChangeGoalCount}
          touched={touched}
          values={values}
        />
      </OptionalGoalsSection>
    </AddGoalsWrapper>
  )
}

AddGoals.propTypes = {
  additionalGoalCount: PropTypes.number,
  errors: PropTypes.object,
  handleChange: PropTypes.func,
  handleChangeGoalCount: PropTypes.func,
  hasApiError: PropTypes.bool,
  interactedFields: PropTypes.object,
  isValid: PropTypes.bool,
  modalData: PropTypes.object,
  preferences: PropTypes.object,
  touched: PropTypes.object,
  values: PropTypes.object,
}

export default AddGoals
